import type { Color } from './color'

const DARKEST_TO_LIGHTEST: readonly (keyof Scale.Colors)[] = [
  'darkest',
  'darker',
  'dark',
  'neutral',
  'light',
  'lighter',
  'lightest',
]

/**
 * Represents a given hue with all of its shades/tints
 */
export class Scale<const TColor extends Color = Color>
  implements Scale.Colors<TColor>
{
  /**
   * The main {@link Scale} for our theme
   */
  public static readonly PRIMARY = new Scale({
    colors: {
      /* eslint-disable sort-keys */
      darkest: '#1D2669',
      darker: '#283593',
      dark: '#3F51B5',
      neutral: '#5C6BC0',
      light: '#7986CB',
      lighter: '#C5CAE9',
      lightest: '#E8EAF6',
      /* eslint-enable sort-keys */
    },
    main: 'dark',
  })
  /**
   * The accent {@link Scale} for our theme
   */
  public static readonly SECONDARY = new Scale({
    colors: {
      /* eslint-disable sort-keys */
      darkest: '#601B3D',
      darker: '#8C2247',
      dark: '#B92D5D',
      neutral: '#CF3164',
      light: '#E44C7D',
      lighter: '#F5BDD1',
      lightest: '#FAE5EC',
      /* eslint-enable sort-keys */
    },
    main: 'dark',
  })
  /**
   * Represents completed, validated, approved, confirmed, etc.
   *
   * Part of our stoplight colors with {@link WARNING} and {@link DANGER}
   */
  public static readonly SUCCESS = new Scale({
    colors: {
      /* eslint-disable sort-keys */
      darkest: '#144239',
      darker: '#1A574E',
      dark: '#207065',
      neutral: '#25897D',
      light: '#3AA69B',
      lighter: '#B5DFDB',
      lightest: '#E1F2F1',
      /* eslint-enable sort-keys */
    },
    main: 'light',
  })
  /**
   * Represents considerations, concerns, caveats, cancellations, unknowns,
   * risks, etc.
   *
   * Part of our stoplight colors with {@link SUCCESS} and {@link DANGER}
   */
  public static readonly WARNING = new Scale({
    colors: {
      /* eslint-disable sort-keys */
      darkest: '#C76812',
      darker: '#E89D22',
      dark: '#FBC02D',
      neutral: '#FFDB35',
      light: '#FCEC55',
      lighter: '#FFF9C4',
      lightest: '#FFFDE7',
      /* eslint-enable sort-keys */
    },
    main: 'dark',
  })
  /**
   * Represents errors, failures, rejections, terminations, etc.
   *
   * Part of our stoplight colors with {@link SUCCESS} and {@link WARNING}
   */
  public static readonly DANGER = new Scale({
    colors: {
      /* eslint-disable sort-keys */
      darkest: '#752622',
      darker: '#B03B37',
      dark: '#CD4643',
      neutral: '#E96361',
      light: '#DE7E80',
      lighter: '#FBD1D7',
      lightest: '#FDEDF0',
      /* eslint-enable sort-keys */
    },
    main: 'dark',
  })
  /**
   * Represents notes, descriptions, explanations, hints, etc.
   */
  public static readonly INFO = new Scale({
    colors: {
      /* eslint-disable sort-keys */
      darkest: '#005E76',
      darker: '#007FA3',
      dark: '#389BC2',
      neutral: '#5CAAC0',
      light: '#79B9CB',
      lighter: '#C5E1E9',
      lightest: '#E8F3F6',
      /* eslint-enable sort-keys */
    },
    main: 'darker',
  })
  /**
   * Used as a darker or more neutral accent color
   */
  public static readonly SHADOW = new Scale({
    colors: {
      /* eslint-disable sort-keys */
      darkest: '#263238',
      darker: '#37474F',
      dark: '#455A64',
      neutral: '#607D8B',
      light: '#90A4AE',
      lighter: '#CFD8DC',
      lightest: '#ECEFF1',
      /* eslint-enable sort-keys */
    },
    main: 'dark',
  })
  /** All {@link Scale} */
  public static readonly ALL = [
    Scale.PRIMARY,
    Scale.SECONDARY,
    Scale.SUCCESS,
    Scale.WARNING,
    Scale.DANGER,
    Scale.INFO,
    Scale.SHADOW,
  ]

  public get darkest(): TColor {
    return this._params.colors.darkest
  }
  public get darker(): TColor {
    return this._params.colors.darker
  }
  public get dark(): TColor {
    return this._params.colors.dark
  }
  public get neutral(): TColor {
    return this._params.colors.neutral
  }
  public get light(): TColor {
    return this._params.colors.light
  }
  public get lighter(): TColor {
    return this._params.colors.lighter
  }
  public get lightest(): TColor {
    return this._params.colors.lightest
  }

  public get main(): TColor {
    return this._params.colors[this._params.main]
  }

  public get all(): readonly TColor[] {
    return DARKEST_TO_LIGHTEST.map(key => this._params.colors[key])
  }

  /**
   * Used by {@link shadeFromMain} and {@link tintFromMain}
   */
  private readonly _mainPosition: number

  /**
   * Used by {@link shade} and {@link tint}
   */
  private readonly _neutralPosition: number

  constructor(private readonly _params: Scale.constructor.Params<TColor>) {
    this._mainPosition = DARKEST_TO_LIGHTEST.indexOf(_params.main)
    this._neutralPosition = DARKEST_TO_LIGHTEST.indexOf('neutral')
  }

  /**
   * Returns the color that is at the given shade
   *
   * \+ = darker than neutral
   *
   * 0 = neutral
   *
   * \- = lighter than neutral
   *
   * @see {@link tint}
   */
  public shade(shades: number): TColor {
    return this._atPosition(this._neutralPosition - shades)
  }

  /**
   * Returns the color that is at the given tint
   *
   * \+ = lighter than neutral
   *
   * 0 = neutral
   *
   * \- = darker than neutral
   *
   * @see {@link shade}
   */
  public tint(tints: number): TColor {
    return this._atPosition(this._neutralPosition + tints)
  }

  /**
   * Returns the color that is a given number of shades darker than {@link main}
   * (up to the darkest available)
   *
   * @see {@link tintFromMain}
   */
  public shadeFromMain(shades = 1): TColor {
    return this._atPosition(this._mainPosition - shades)
  }

  /**
   * Returns the color that is a given number of tints lighter than {@link main}
   * (up to the lightest available)
   *
   * @see {@link shadeFromMain}
   */
  public tintFromMain(tints = 1): TColor {
    return this._atPosition(this._mainPosition + tints)
  }

  /**
   * Returns the color that is a given number of shades darker than
   * {@link lightest} (up to the darkest available)
   *
   * @see {@link tintFromMain}
   */
  public shadeFromLightest(shades = 1): TColor {
    return this._atPosition(DARKEST_TO_LIGHTEST.length - shades - 1)
  }

  /**
   * Returns the color that is a given number of tints lighter than
   * {@link darkest} (up to the lightest available)
   *
   * @see {@link shadeFromMain}
   */
  public tintFromDarkest(tints = 1): TColor {
    return this._atPosition(tints)
  }

  /**
   * Returns the color at the given position
   *
   * If the position is invalid (i.e. too big or too small), it returns the
   * closest color to that position
   */
  private _atPosition(position: number): TColor {
    position = Math.max(
      Math.min(Math.round(position), DARKEST_TO_LIGHTEST.length - 1),
      0,
    )
    // NOTE: this implicitly handles invalid values because indexing with an
    // improper position or key will yield `undefined`
    const key = DARKEST_TO_LIGHTEST[position]
    return this._params.colors[key]
  }
}
export namespace Scale {
  export namespace constructor {
    export interface Params<TColor extends Color = Color> {
      readonly colors: Colors<TColor>
      readonly main: keyof Colors<TColor>
    }
  }

  export interface Colors<TColor extends Color = Color> {
    readonly darkest: TColor
    readonly darker: TColor
    readonly dark: TColor
    readonly neutral: TColor
    readonly light: TColor
    readonly lighter: TColor
    readonly lightest: TColor
  }
}
